import React from "react";
import clone from "lodash/clone";
import cloneDeep from "lodash/cloneDeep";
import { useQueryClient } from "@tanstack/react-query";
import i18n from "i18n";
import { ConfirmLeaveDialog } from "components/common";
import Routes from "routing/Routes";
import {
  createEventNotification,
  updateEventNotification,
  testEventNotification,
  EVENT_NOTIFICATIONS_QUERY_KEY
} from "components/event-notifications/hooks/useEventNotifications";
import withHistory from "routing/withHistory";
import CurrentUser from "logic/users/CurrentUser";
import EventNotificationForm from "./EventNotificationForm";
import { translateValidationResult } from "util/translateApiError";
const initialValidation = {
  errors: {}
};
const initialTestResult = {
  isLoading: false
};
const _EventNotificationFormContainer = class _EventNotificationFormContainer extends React.Component {
  constructor(props) {
    super(props);
    this.handleChange = (key, value) => {
      const { notification } = this.state;
      const nextNotification = cloneDeep(notification);
      nextNotification[key] = value;
      this.setState({ notification: nextNotification, isDirty: true, testResult: initialTestResult });
    };
    this.handleCancel = () => {
      const { history } = this.props;
      history.push(Routes.ALERTS.NOTIFICATIONS.LIST);
    };
    this.handleSubmit = () => {
      const { action, embedded, onSubmit, history, queryClient } = this.props;
      const { notification } = this.state;
      this.setState({ isDirty: false });
      let promise;
      if (action === "create") {
        promise = createEventNotification(notification);
        promise.then((response) => {
          queryClient.invalidateQueries({ queryKey: EVENT_NOTIFICATIONS_QUERY_KEY });
          CurrentUser.reload();
          return response;
        }).then(
          () => {
            if (!embedded) {
              history.push(Routes.ALERTS.NOTIFICATIONS.LIST);
            }
          },
          (errorResponse) => {
            const { body } = errorResponse.additional;
            if (errorResponse.status === 400 && body && body.failed) {
              this.setState({ validation: translateValidationResult(body) });
              _EventNotificationFormContainer.scrollToFirstError();
            }
          }
        );
      } else {
        promise = updateEventNotification(notification.id, notification);
        promise.then(
          () => {
            queryClient.invalidateQueries({ queryKey: EVENT_NOTIFICATIONS_QUERY_KEY });
            if (!embedded) {
              history.push(Routes.ALERTS.NOTIFICATIONS.LIST);
            }
          },
          (errorResponse) => {
            const { body } = errorResponse.additional;
            if (errorResponse.status === 400 && body && body.failed) {
              this.setState({ validation: translateValidationResult(body) });
              _EventNotificationFormContainer.scrollToFirstError();
            }
          }
        );
      }
      onSubmit(promise);
    };
    this.handleTest = () => {
      const { notification } = this.state;
      this.setState({ testResult: { isLoading: true }, validation: initialValidation });
      const testResult = clone(initialTestResult);
      this.testPromise = testEventNotification(notification);
      this.testPromise.then(
        (response) => {
          testResult.error = false;
          testResult.message = i18n.t("test.success", { ns: "event_notifications" });
          return response;
        },
        (errorResponse) => {
          testResult.error = true;
          const { body } = errorResponse.additional;
          if (errorResponse.status === 400 && body && body.failed) {
            testResult.message = i18n.t("test.validation_failed", { ns: "event_notifications" });
            this.setState({ validation: translateValidationResult(body) });
          } else {
            testResult.message = errorResponse.responseMessage || i18n.t("test.unknown_error", { ns: "event_notifications" });
          }
        }
      ).finally(() => {
        this.setState({ testResult });
        this.testPromise = void 0;
      });
    };
    this.state = {
      notification: props.notification,
      validation: initialValidation,
      testResult: initialTestResult,
      isDirty: false
    };
  }
  static scrollToFirstError() {
    if (document.getElementsByClassName("has-error")[0] !== void 0) {
      document.getElementsByClassName("has-error")[0].scrollIntoView(true);
    }
  }
  componentWillUnmount() {
    if (this.testPromise) {
      this.testPromise.cancel();
    }
  }
  render() {
    const { action, embedded, formId } = this.props;
    const { notification, validation, testResult, isDirty } = this.state;
    return /* @__PURE__ */ React.createElement(React.Fragment, null, !embedded && isDirty && /* @__PURE__ */ React.createElement(ConfirmLeaveDialog, { question: i18n.t("form.confirm_leave", { ns: "event_notifications" }) }), /* @__PURE__ */ React.createElement(
      EventNotificationForm,
      {
        action,
        notification,
        validation,
        testResult,
        formId,
        embedded,
        onChange: this.handleChange,
        onCancel: this.handleCancel,
        onSubmit: this.handleSubmit,
        onTest: this.handleTest
      }
    ));
  }
};
_EventNotificationFormContainer.defaultProps = {
  action: "edit",
  notification: {
    title: "",
    description: "",
    config: {}
  },
  embedded: false,
  formId: void 0,
  onSubmit: () => {
  }
};
let EventNotificationFormContainer = _EventNotificationFormContainer;
const EventNotificationFormContainerWithHistory = withHistory(EventNotificationFormContainer);
const EventNotificationFormContainerWithQueryClient = (props) => {
  const queryClient = useQueryClient();
  return /* @__PURE__ */ React.createElement(EventNotificationFormContainerWithHistory, { ...props, queryClient });
};
export default EventNotificationFormContainerWithQueryClient;
